import { computed, toValue, type MaybeRefOrGetter, type Ref, toRef } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { ANYWHERE_GROUP_CODE, splitBy, type StationGroupType } from '@cvo/domain';
import type { AutocompleteGroupOption, AutocompleteOption } from '../components/molecules/Autocomplete/Autocomplete.model';

export interface RouteOption {
  origin: string;
  destinations: string[];
}

/** A selectable station group with its member station codes, derived from the availability routes. */
export interface DestinationGroupOption {
  code: string;
  /** Sub-classification of the group, driving whether picking it lists flights (MAC) or opens the city grid. */
  groupType?: StationGroupType;
  stations: string[];
}

/** A grid group option rendered as a Tag chip in the destination autocomplete prepend slot. */
export interface CustomGroupOption {
  label: string;
  value: string;
  leftIcon?: string;
}

function buildCustomGroupOptions(
  groups: DestinationGroupOption[] | null | undefined,
  applicable: Set<string>,
  resolve: { label: (code: string) => string; icon: (code: string) => string | undefined },
): CustomGroupOption[] {
  if (!groups?.length) return [];
  return groups
    .filter(group => group.groupType === 'CUSTOM_GROUP' && applicable.has(group.code))
    .map(group => ({ label: resolve.label(group.code), value: group.code, leftIcon: resolve.icon(group.code) }));
}

/**
 * A group whose header, when selected, browses a multi-city result grid (rather than a flat list).
 */
function isCustomGroup(groupType: StationGroupType | undefined): boolean {
  return groupType === 'CUSTOM_GROUP' || groupType === 'ANYWHERE';
}

function sortByLabel<T extends { label: string }>(options: T[]): T[] {
  return options.sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));
}


export function useRouteOptions(
  routes: MaybeRefOrGetter<RouteOption[]>,
  initialValues?: { origin?: Ref<string | undefined>; destination?: Ref<string | undefined> },
  destinationGroups?: MaybeRefOrGetter<DestinationGroupOption[] | null | undefined>,
  options?: { enableBrowseGroups?: MaybeRefOrGetter<boolean>; originGroups?: MaybeRefOrGetter<DestinationGroupOption[] | null | undefined> },
) {
  const { t, tf } = useI18n();

  const origin: Ref<string | undefined> = toRef(initialValues?.origin);
  const destination: Ref<string | undefined> = toRef(initialValues?.destination);

  // Helper function to map destinations to autocomplete options
  function mapDestinationsToOptions(iataCodes: string[]): Array<AutocompleteOption<string>> {
    return sortByLabel(iataCodes.map(dest => ({
      label: dest === ANYWHERE_GROUP_CODE ? t('stationGroups.ANYWHERE.name') : `${t(`airports.${dest}.name`)} (${dest})`,
      value: dest,
    })));
  }

  // Build a group header whose member stations become sub-items. When the group code itself is
  // in the list (a bookable group destination) all its stations are shown; otherwise only the
  // members present in the list are clustered (visual grouping of airports).
  //
  // Header selectability: origins never select the header (`enableGroups` is false → visual grouping
  // only). For destinations, a MAC header is always selectable (lists the metro's flights); a grid
  // group (CUSTOM_GROUP) is selectable only when browse mode is enabled, otherwise it degrades to a
  // visual grouping over its still-selectable member airports.
  function isGroupHeaderSelectable(group: DestinationGroupOption, enableGroups: boolean): boolean {
    if (!enableGroups) return false;
    if (!isCustomGroup(group.groupType)) return true;

    return toValue(options?.enableBrowseGroups) === true;
  }

  function buildGroupOption(group: DestinationGroupOption, codeSet: Set<string>, enableGroups: boolean): AutocompleteGroupOption<string> | undefined {
    const hasGroupCode = codeSet.has(group.code);
    const memberCodes = hasGroupCode ? group.stations : group.stations.filter(station => codeSet.has(station));

    if (!hasGroupCode && memberCodes.length === 0) return undefined;

    return {
      label: tf(`stationGroups.${group.code}.name`, group.code),
      value: group.code,
      disabled: !isGroupHeaderSelectable(group, enableGroups),
      items: mapDestinationsToOptions(memberCodes),
    };
  }

  // Cluster station groups into headers with their member stations as sub-items. Members are
  // consumed so they never appear twice (flat + nested). `enableGroups` makes the header itself
  // selectable; disabled headers act as visual grouping only. As per @rdr requested, both
  // origin and destination allow group selection.
  function buildGroupedOptions(
    iataCodes: string[],
    groupsSource: MaybeRefOrGetter<DestinationGroupOption[] | null | undefined>,
    enableGroups: boolean = false,
  ): Array<AutocompleteOption<string> | AutocompleteGroupOption<string>> {
    const codes = iataCodes.filter(code => code !== ANYWHERE_GROUP_CODE);
    const groups = toValue(groupsSource);

    if (!groups?.length) return mapDestinationsToOptions(codes);

    const codeSet = new Set(codes);
    // Grid groups (CUSTOM_GROUP/ANYWHERE) are rendered as chips elsewhere, not as autocomplete headers, once browse mode is on.
    const browseGroupsEnabled = toValue(options?.enableBrowseGroups) === true;
    const [skippedGridGroups, candidateGroups] = browseGroupsEnabled
      ? splitBy(groups, group => isCustomGroup(group.groupType))
      : [[], groups];

    const groupOptions = candidateGroups
      .map(group => buildGroupOption(group, codeSet, enableGroups))
      .filter((option): option is AutocompleteGroupOption<string> => option !== undefined);

    const consumed = new Set([
      ...skippedGridGroups.map(group => group.code),
      ...groupOptions.flatMap(option => [option.value, ...option.items.map(item => item.value)]), // Get the group and its child items codes
    ]);

    const flatOptions = mapDestinationsToOptions(codes.filter(code => !consumed.has(code)));
    return sortByLabel([...groupOptions, ...flatOptions]);
  }

  // ===== Departure (outbound) options ===== //

  const departureAvailableFromDestination = computed<string[] | undefined>(() => {
    if (!destination.value || destination.value === ANYWHERE_GROUP_CODE) return undefined;

    const routeList = toValue(routes);

    return routeList
      .filter(route => route.destinations.includes(destination.value!))
      .map(route => route.origin);
  });

  const allOrigins = computed<string[]>(() => {
    const routeList = toValue(routes);

    return routeList.map(route => route.origin);
  });

  // allow group selection in origin as per @rdr asked
  const originOptions = computed<Array<AutocompleteOption<string> | AutocompleteGroupOption<string>>>(() => {
    return buildGroupedOptions(departureAvailableFromDestination.value ?? allOrigins.value, options?.originGroups ?? destinationGroups, true);
  });

  // ===== Destination (inbound) options ===== //

  const allDestinations = computed<string[]>(() => {
    const routeList = toValue(routes);

    const destinations = routeList.flatMap(route => route.destinations);
    return Array.from(new Set(destinations));
  });

  const destinationsAvailableFromDeparture = computed<string[] | undefined>(() => {
    if (!origin.value) return undefined;

    const routeList = toValue(routes);
    const selectedRoute = routeList.find(route => route.origin === origin.value);

    return selectedRoute?.destinations;
  });

  const destinationOptions = computed<Array<AutocompleteOption<string> | AutocompleteGroupOption<string>>>(() => {
    return buildGroupedOptions(destinationsAvailableFromDeparture.value ?? allDestinations.value, destinationGroups, true);
  });

  const customGroupOptions = computed<CustomGroupOption[]>(() => {
    if (!toValue(options?.enableBrowseGroups)) return [];
    const applicable = new Set(destinationsAvailableFromDeparture.value ?? allDestinations.value);
    return buildCustomGroupOptions(toValue(destinationGroups), applicable, {
      label: code => tf(`stationGroups.${code}.name`, code),
      icon: code => tf(`stationGroups.${code}.icon`),
    });
  });

  return {
    origin,
    destination,
    originOptions,
    destinationOptions,
    allOrigins,
    allDestinations,
    anywhereOption: mapDestinationsToOptions([ANYWHERE_GROUP_CODE])[0],
    customGroupOptions,
  };
}